import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';
import {
  Branch,
  BranchApi,
  UpdateBranch,
  BranchFilters,
  CreateBranchPayload,
} from '../models';
import { mapBranchApiToModel } from '../adapters';

const buildFilterParams = (filters?: BranchFilters) => {
  if (!filters) return {};

  return {
    ...(filters.name &&
      filters.name.trim() !== '' && { name: filters.name.trim() }),
    ...(filters.businessName &&
      filters.businessName.trim() !== '' && {
        businessName: filters.businessName.trim(),
      }),
    ...(filters.externalId &&
      filters.externalId.trim() !== '' && {
        externalId: filters.externalId.trim(),
      }),
    ...(filters.enabled !== undefined && { enabled: filters.enabled }),
    ...(filters.cityId &&
      filters.cityId.trim() !== '' && { cityId: filters.cityId.trim() }),
    ...(filters.neighborhoodId &&
      filters.neighborhoodId.trim() !== '' && {
        neighborhoodId: filters.neighborhoodId.trim(),
      }),
    ...(filters.stateOrDepartmentId &&
      filters.stateOrDepartmentId.trim() !== '' && {
        stateOrDepartmentId: filters.stateOrDepartmentId.trim(),
      }),
    ...(filters.countryId &&
      filters.countryId.trim() !== '' && {
        countryId: filters.countryId.trim(),
      }),
    ...(filters.commerceId &&
      filters.commerceId.trim() !== '' && {
        commerceId: filters.commerceId.trim(),
      }),
    ...(filters.commerceIds !== undefined && {
      commerceIds: filters.commerceIds.join(','),
    }),
  };
};

const branchesService = createApi({
  reducerPath: 'branchesService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['Branch', 'Branches'] as const,
  endpoints: (builder) => ({
    getBranchesByCommerce: builder.query<
      PaginatedResponse<Branch>,
      {
        commerceId: string;
        paginated: PaginatedRequest;
        filters?: BranchFilters;
      }
    >({
      query: ({ commerceId, paginated, filters }) => ({
        url: '/branches',
        params: {
          commerceId,
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...buildFilterParams(filters),
        },
      }),
      transformResponse: (response: PaginatedResponse<BranchApi>) => ({
        ...response,
        items: response.items.map(mapBranchApiToModel),
      }),
      providesTags: ['Branches'],
    }),
    getAllBranches: builder.query<
      PaginatedResponse<Branch>,
      {
        paginated: PaginatedRequest;
        filters?: BranchFilters;
      }
    >({
      query: ({ paginated, filters }) => ({
        url: '/branches',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...buildFilterParams(filters),
        },
      }),
      transformResponse: (response: PaginatedResponse<BranchApi>) => ({
        ...response,
        items: response.items.map(mapBranchApiToModel),
      }),
      providesTags: ['Branches'],
    }),
    getBranchById: builder.query<Branch, { branchId: string }>({
      query: ({ branchId }) => ({
        url: `/branches/${branchId}`,
        params: {
          include:
            'address,commerce,address.lat,address.long,address.neighborhood,address.neighborhood.city,address.neighborhood.city.stateOrDepartment',
        },
      }),
      transformResponse: (response: BranchApi) =>
        mapBranchApiToModel(response),
      providesTags: (_result, _error, arg) => [
        { type: 'Branch', id: arg.branchId },
        'Branches',
      ],
    }),
    createBranch: builder.mutation<Branch, CreateBranchPayload>({
      query: (body) => ({
        url: '/branches',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Branches'],
    }),
    updateBranch: builder.mutation<
      Branch,
      { branchId: string; data: UpdateBranch }
    >({
      query: ({ branchId, data }) => ({
        url: `/branches/${branchId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Branch', id: arg.branchId },
        'Branches',
      ],
    }),
    deleteBranch: builder.mutation<void, string>({
      query: (branchId) => ({
        url: `/branches/${branchId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Branch', 'Branches'],
    }),
  }),
});

export default branchesService;
export const {
  useGetBranchesByCommerceQuery,
  useGetAllBranchesQuery,
  useGetBranchByIdQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
} = branchesService;
